import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Alert, App, Modal } from 'antd';
import { useState } from 'react';
import type { PlaceDto } from '@pams/shared';
import { api, type ApiError } from '@/lib/api';
import { queryKeys } from '@/lib/queryKeys';
import { PlacePicker } from './PlacePicker';

/**
 * Merge another place INTO this one. SUPER_ADMIN only, via `place.merge`.
 *
 * The modal states the consequence before it happens, in the shape `MoveUnitModal` uses
 * for a reparent — the two acts are the same kind: cheap to perform, wide in effect, and
 * not undoable by editing a field back.
 *
 * What it must make clear, because it is the part operators get wrong: the absorbed
 * place is NOT deleted. Its name becomes an alias of this one and keeps answering in
 * search, and its id keeps resolving — to this place. That is what makes merging the
 * right response to a duplicate rather than a destructive one.
 */
export const MergePlaceModal = ({
  place,
  onClose,
  onSuccess,
}: {
  place: PlaceDto;
  onClose: () => void;
  onSuccess?: () => void;
}) => {
  const queryClient = useQueryClient();
  const { message } = App.useApp();
  const [sourceId, setSourceId] = useState<string | undefined>();
  const [error, setError] = useState<string | undefined>();

  const merge = useMutation({
    mutationFn: (id: string) => api.post<PlaceDto>(`/places/${place.id}/merge`, { sourceId: id }),
    onSuccess: () => {
      void message.success('Places merged');
      void queryClient.invalidateQueries({ queryKey: queryKeys.places.all });
      onSuccess?.();
      onClose();
    },
    onError: (apiError: ApiError) => setError(apiError.message),
  });

  return (
    <Modal
      open
      title={`Merge into ${place.name}`}
      okText="Merge"
      okButtonProps={{ disabled: !sourceId, danger: true }}
      confirmLoading={merge.isPending}
      onOk={() => {
        if (sourceId) merge.mutate(sourceId);
      }}
      onCancel={onClose}
    >
      {error && <Alert type="error" showIcon className="mb-4" message={error} />}

      <Alert
        type="info"
        showIcon
        className="mb-4"
        message="The place you choose stops being a separate record."
        description={
          <span>
            Its name and any other names it answers to become alternative names for{' '}
            <span className="font-medium">{place.name}</span>, so searches for the old name still find this place. Its
            usage count is added to this one, and anything already pointing at it resolves here. This cannot be undone
            from the interface.
          </span>
        }
      />

      <label className="flex flex-col gap-1">
        <span className="text-xs font-medium text-muted-foreground">Place to merge in</span>
        <PlacePicker
          value={sourceId}
          onChange={setSourceId}
          excludeId={place.id}
          placeholder="Search for the duplicate"
          data-testid="merge-source-picker"
        />
      </label>
    </Modal>
  );
};
